import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EnergiesService, Range, Inverter } from 'ngx-core-services';
import { map, switchMap, catchError, withLatestFrom, mapTo, first } from 'rxjs/operators';
import { of, from } from 'rxjs';

import {
  AppState,
  SystemsState,
  getSystemDefinitionDeviceIdsForTelemetries,
  getSystemDefinitionConfiguration,
  requestSystemEnergyDataOverview,
  selectedSystemMetadata,
  setSystemEnergyDataOverviewError,
  updateSystemEnergyDataOverview,
} from '..';

@Injectable()
export class EnergyDataOverviewEffects {

  requestEnergyDataOverviewEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestSystemEnergyDataOverview),
      switchMap((action) =>
        this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        ),
      ),
      withLatestFrom(this.store),
      switchMap(([action, { systemState }]) => {
        return from(this.getEnergyDataOverview(
            action.systemId,
            action.range,
            action.mode,
            action.forceRefresh,
            systemState,
          )
        ).pipe(
          map((systemEnergyState: {
            energies: Partial<Inverter>[],
            mode: 'day' | 'month' | 'year',
          }) => updateSystemEnergyDataOverview({ systemId: action.systemId, instanceId: action.instanceId, range: action.range, systemEnergyState: systemEnergyState.energies, mode: systemEnergyState.mode })),
          catchError(_ => of(setSystemEnergyDataOverviewError({ systemId: action.systemId, instanceId: action.instanceId, error: 'Failed to fetch system energy state' })))
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly energiesService: EnergiesService,
  ) {}

  async getEnergyDataOverview(systemId: string, range: Range, mode: 'day' | 'year' | 'month',  forceRefresh: boolean, state: SystemsState): Promise<{
    energies: Partial<Inverter>[],
    mode: 'day' | 'month' | 'year',
  }> {
    const system = state.systemList[systemId];

    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);
    const serials = getSystemDefinitionDeviceIdsForTelemetries(system);

    const energies = await this.energiesService.fetchEnergiesByRange(serials, systemConfiguration.monitoringType, range, forceRefresh);
    let aggregateEnergies = this.energiesService.getEnergyAggregate(energies, !!systemConfiguration.inverterMeterSensor);
    return {
      energies: aggregateEnergies,
      mode,
    };
  }
}
